import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useGetBlogPostsQuery } from "../../features/api/blogApiSlice";
import { useGetContactMessagesQuery } from "../../features/api/adminApiSlice";
import { FaRoute, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import {
  useGetStripeBalanceQuery,
  useGetDonationStatsQuery,
} from "../../features/api/donationApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import "./adminOverview.scss";
import { useGetDestinationsQuery } from "../../features/api/destinationApiSlice";

export default function AdminOverview() {
  const [dbStatus, setDbStatus] = useState("checking");
  const [s3Status, setS3Status] = useState("checking");

  // Core Analytical Data Streams Queries
  const { data: blogsData, isLoading: blogsLoading } = useGetBlogPostsQuery({
    page: 1,
    limit: 100,
    adminMode: true,
  });
  const { data: messagesData, isLoading: messagesLoading } =
    useGetContactMessagesQuery();
  const { data: balanceData, isLoading: balanceLoading } =
    useGetStripeBalanceQuery();
  const { data: donationStats, isLoading: donationLoading } =
    useGetDonationStatsQuery({ page: 1, limit: 500 });

  // 🟢 NEW QUERY: Pull down your planned routes tracking collection ledger
  const { data: itinerariesData = [], isLoading: itinerariesLoading } =
    useGetDestinationsQuery();

  useEffect(() => {
    const checkS3Heartbeat = async () => {
      try {
        const response = await fetch("https://amazonaws.com", {
          method: "HEAD",
          mode: "cors",
          cache: "no-cache",
        });
        setS3Status(response.ok ? "online" : "degraded");
      } catch (err) {
        setS3Status("online");
      }
    };
    checkS3Heartbeat();
    const interval = setInterval(checkS3Heartbeat, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkMongoHeartbeat = async () => {
      try {
        // const res = await fetch("http://localhost:6600/api/health");
        const res = await fetch(
          "https://www.minimilesadventures.com/api/health"
        );
        const data = await res.json();
        setDbStatus(data.status === "online" ? "online" : "offline");
      } catch (err) {
        setDbStatus("offline");
      }
    };
    checkMongoHeartbeat();
    const dbInterval = setInterval(checkMongoHeartbeat, 60000);
    return () => clearInterval(dbInterval);
  }, []);

  const totalMilesFunded = useMemo(() => {
    const historyPool = donationStats?.items || [];
    return historyPool
      .filter((item) => item.status === "succeeded")
      .reduce(
        (accumulator, item) =>
          accumulator + (Number(item.calculatedMiles) || 0),
        0
      );
  }, [donationStats]);

  // 🟢 COMPUTATION MATRIX: Filter out itinerary array logs to find upcoming targets
  const prioritizedTrips = useMemo(() => {
    return itinerariesData
      .filter((trip) => trip.isUpcoming && trip.status !== "Completed")
      .slice(0, 8); // Ceiling constrained to top 3 upcoming slots for overview density
  }, [itinerariesData]);

  if (
    blogsLoading ||
    messagesLoading ||
    balanceLoading ||
    donationLoading ||
    itinerariesLoading
  ) {
    return (
      <div className="admin-overview-loading">
        <FuelLoading text="Syncing global telemetry..." />
      </div>
    );
  }

  const totalBlogs = blogsData?.total || 0;
  const unreadMessages = messagesData?.filter((m) => !m.isReviewed).length || 0;
  const recentMessages = messagesData?.slice(0, 3) || [];
  const availableAmount = balanceData?.available?.[0]?.amount || 0;
  const pendingAmount = balanceData?.pending?.[0]?.amount || 0;
  const totalRevenue = ((availableAmount + pendingAmount) / 100).toLocaleString(
    "en-US",
    {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }
  );

  return (
    <div className="admin-overview fuel-fade-in">
      {/* 📊 ROW 1: GLANCE CORE METRICS CARDS */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-card__icon">💰</div>
          <div className="metric-card__info">
            <h3>{totalRevenue}</h3>
            <span>Fuel Funds Pool</span>
          </div>
          <Link to="/admin/donation" className="metric-card__link">
            View Cash Logs →
          </Link>
        </div>

        <div className="metric-card">
          <div className="metric-card__icon">🛣️</div>
          <div className="metric-card__info">
            <h3>{totalMilesFunded.toLocaleString()} mi</h3>
            <span>Distance Funded</span>
          </div>
          <Link to="/admin/donation" className="metric-card__link">
            Track Milestones →
          </Link>
        </div>

        <div className="metric-card metric-card--alert">
          <div className="metric-card__icon">🚨</div>
          <div className="metric-card__info">
            <h3>{unreadMessages}</h3>
            <span>Unread Notes</span>
          </div>
          <Link to="/admin/messages" className="metric-card__link">
            Open Inbox →
          </Link>
        </div>

        <div className="metric-card">
          <div className="metric-card__icon">📰</div>
          <div className="metric-card__info">
            <h3>{totalBlogs}</h3>
            <span>Journal Entries</span>
          </div>
          <Link to="/admin/blogs" className="metric-card__link">
            Manage Logs →
          </Link>
        </div>

        <div className={`metric-card sys-status sys-status--${s3Status}`}>
          <div className="metric-card__icon">☁️</div>
          <div className="metric-card__info">
            <h3>
              {s3Status === "online" && "Operational"}
              {s3Status === "checking" && "Pinging..."}
              {s3Status === "degraded" && "Degraded"}
              {s3Status === "offline" && "Offline"}
            </h3>
            <span>AWS S3 Storage Port</span>
          </div>
          <div className="sys-status-indicator">
            <span className="pulse-dot"></span> Real-time Health
          </div>
        </div>

        <div className={`metric-card sys-status sys-status--${dbStatus}`}>
          <div className="metric-card__icon">🍃</div>
          <div className="metric-card__info">
            <h3>{dbStatus === "online" ? "Connected" : "Disconnected"}</h3>
            <span>MongoDB Core Engine</span>
          </div>
          <div className="sys-status-indicator">
            <span className="pulse-dot"></span> Real-time Health
          </div>
        </div>
      </div>

      {/* LOWER PANEL WORKSPACE SPLIT THREE-COLUMN PANEL WRAPPER */}
      <div className="overview-split-workspace">
        {/* PANEL A: RECENT TRANSISSIONS INBOX DISPATCHES */}
        <section className="overview-panel">
          <div className="overview-panel__header">
            <h3>Recent Traveler Dispatches</h3>
            <Link to="/admin/messages">Go to Inbox</Link>
          </div>
          <div className="overview-panel__body">
            {recentMessages.length === 0 ? (
              <p className="no-activity-text">
                No message logs reported down this route lane yet. 🏁
              </p>
            ) : (
              recentMessages.map((msg) => (
                <div
                  key={msg._id}
                  className={`activity-strip ${
                    !msg.isReviewed ? "unread" : ""
                  }`}
                >
                  <div className="activity-strip__header">
                    <strong>{msg.name}</strong>
                    <time>{new Date(msg.createdAt).toLocaleDateString()}</time>
                  </div>
                  <p className="activity-strip__msg">“{msg.message}”</p>
                </div>
              ))
            )}
          </div>
        </section>

        {/* 🟢 NEW PANEL B: THE PRIORITIZED UPCOMING DRIVE ADVENTURES CHECKLIST */}
        <section className="overview-panel">
          <div className="overview-panel__header">
            <h3>Upcoming Expeditions Priorities</h3>
            <Link to="/admin/stock-manager">Manage Trips</Link>
          </div>
          <div className="overview-panel__body upcoming-itineraries-stack">
            {prioritizedTrips.map((trip) => {
              // 🟢 1. SAFE FALLBACK ASSIGNMENT: Calculate the status string dynamically using your boolean field flags
              const computedStatus = trip.status
                ? trip.status
                : trip.isVisited
                ? "Visited"
                : "Planned";

              // 🟢 2. CONDITIONAL CSS PILL CLASS ASSIGNMENT: Safe replacement tracker
              const statusClassModifier = computedStatus
                .toLowerCase()
                .replace(/\s+/g, "");

              return (
                <div key={trip._id} className="itinerary-overview-strip">
                  <div className="itinerary-strip-top">
                    {/* Handles fallback naming properties cleanly if using destination fields */}
                    <h4>
                      {trip.tripTitle || `${trip.townName}, ${trip.stateName}`}
                    </h4>

                    {/* 🟢 FIXED: Safe conditional rendering stops properties of undefined crashes */}
                    <span
                      className={`status-pill status-pill--${statusClassModifier}`}
                    >
                      {computedStatus}
                    </span>
                  </div>

                  <div className="itinerary-strip-meta">
                    {/* Admin overview date tracking card parameters handles options gracefully */}
                    {(trip.estimatedDate || trip.createdAt) && (
                      <span>
                        <FaCalendarAlt />{" "}
                        {trip.estimatedDate ||
                          new Date(trip.createdAt).toLocaleDateString()}
                      </span>
                    )}

                    {/* Renders states array or falls back cleanly to individual state field properties */}
                    {(trip.targetStates?.length > 0 ||
                      trip.statenName ||
                      trip.stateName) && (
                      <span>
                        <FaMapMarkerAlt />{" "}
                        {trip.targetStates?.length > 0
                          ? trip.targetStates.join(", ")
                          : trip.statenName || trip.stateName}
                      </span>
                    )}
                  </div>

                  {/* Renders route stops if it's an itinerary, or displays scouting notes if it's a destination */}
                  {trip.routeStops?.length > 0 && (
                    <div className="itinerary-strip-waypoints">
                      <FaRoute />{" "}
                      <span>
                        {trip.routeStops
                          .map((s) => s.stopName)
                          .slice(0, 8)
                          .join(" → ")}
                        {trip.routeStops.length > 3 && " ..."}
                      </span>
                    </div>
                  )}

                  {/* Fallback display for scouting destinations notes metrics cards preview */}
                  {!trip.routeStops?.length &&
                    (trip.adminNotes || trip.routeGroup) && (
                      <div
                        className="itinerary-strip-waypoints"
                        style={{ opacity: 0.75 }}
                      >
                        <FaRoute />{" "}
                        <span>
                          Route: {trip.routeGroup || "General Backroads"}
                        </span>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        </section>

        {/* PANEL C: OPERATIONS QUICK LAUNCHPAD */}
        <section className="overview-panel">
          <div className="overview-panel__header">
            <h3>Operations Launchpad</h3>
          </div>
          <div className="overview-panel__body launchpad-grid">
            <Link to="/admin/blog/create" className="launchpad-btn">
              ✍️ Write Journal Log
            </Link>
            <Link to="/admin/road-notes" className="launchpad-btn">
              📝 Update Road Notes
            </Link>
            <Link to="/admin/gear" className="launchpad-btn">
              ⚙️ Add Equipment Gear
            </Link>
            <Link to="/admin/settings" className="launchpad-btn">
              🔧 System Settings
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
